import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { EmptyIcon } from 'assets/icon/bigicon';
import TrashIcon from 'assets/icon/dynamic/TrashIcon';
import ModalDelete from 'components/ModalDelete';
import {
  fetchMasterConfigs,
  createMasterConfig,
  deleteMasterConfig,
} from 'state/actions/masterConfig';
import ConfigNameModal from './ConfigNameModal';

// Lists every master config (content type) of the site: edit, delete, add.
function ConfigListView({ onBack, onEditConfig }) {
  const [showNameModal, setShowNameModal] = useState(false);
  const [modalDelete, setModalDelete] = useState({ show: false, config: null });

  const dispatch = useDispatch();
  const { siteId } = useParams();

  const { list, loading } = useSelector((state) => ({
    list: state.masterConfig.list,
    loading: state.masterConfig.loading,
  }));

  useEffect(() => {
    if (siteId) {
      dispatch(fetchMasterConfigs({ siteId }));
    }
  }, [siteId, dispatch]);

  // New config starts empty; go straight to its field editor.
  const handleCreate = (name) => {
    dispatch(
      createMasterConfig({ siteId, configName: name, fields: [] }, (config) => {
        setShowNameModal(false);
        onEditConfig(config._id);
      })
    );
  };

  const handleDelete = () => {
    dispatch(
      deleteMasterConfig(modalDelete.config._id, () =>
        setModalDelete({ show: false, config: null })
      )
    );
  };

  return (
    <div className="dynamic-comp">
      <div className="dynamic-comp-header">
        <h2>Master Config</h2>
        <div className="dynamic-comp-header-actions">
          <button
            className="dynamic-comp-btn-new-field"
            onClick={() => setShowNameModal(true)}
          >
            <i className="feather-plus" />
            New Content Type
          </button>
          <button className="dynamic-comp-btn-back" onClick={onBack}>
            <i className="feather-arrow-left" />
            Back
          </button>
        </div>
      </div>

      {list.length > 0 && (
        <div className="dynamic-comp-field-list">
          {list.map((config) => (
            <div key={config._id} className="dynamic-comp-field-card">
              <div className="dynamic-comp-field-content">
                <span className="dynamic-comp-field-type">
                  {`${config.fields.length} field${
                    config.fields.length === 1 ? '' : 's'
                  }`}
                </span>
                <h4 className="dynamic-comp-field-label">
                  {config.configName || 'Untitled'}
                </h4>
                <p className="dynamic-comp-field-desc">
                  {config.fields.map((f) => f.label).join(' · ')}
                </p>
              </div>
              <div className="dynamic-comp-field-actions">
                <div
                  className="dynamic-comp-field-action"
                  title="Edit"
                  onClick={() => onEditConfig(config._id)}
                >
                  <i className="feather-sliders" />
                </div>
                <div
                  className="dynamic-comp-field-action"
                  title="Delete"
                  onClick={() => setModalDelete({ show: true, config })}
                >
                  <TrashIcon color="#e74c3c" size={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {list.length === 0 && !loading && (
        <div className="dynamic-comp-empty">
          <EmptyIcon className="dynamic-comp-empty-icon" />
          <h3>No Data</h3>
          <p>
            No content type yet. Click "New Content Type" to create one, then
            add its fields.
          </p>
        </div>
      )}

      <ConfigNameModal
        visible={showNameModal}
        onSubmit={handleCreate}
        onCancel={() => setShowNameModal(false)}
      />

      <ModalDelete
        isVisible={modalDelete.show}
        onCancel={() => setModalDelete({ show: false, config: null })}
        onOk={handleDelete}
        title="Delete Content Type"
        subtitle={`Are you sure you want to delete "${modalDelete.config?.configName}"? This only works when it has no children left.`}
      />
    </div>
  );
}

export default ConfigListView;
